import { createSlice, createAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    ReadingPassage,
    ReadingRecommendation,
    ReadingPassageFilters,
    PaginatedResponse,
    UserReadingProfile,
    ComprehensionQuestion,
    FeedbackTone,
} from '@/services/reading/types';
import { START_PAGE } from '@/common/constants';

// === READING ACTIONS ===
export const fetchReadingPassagesRequest = createAction<ReadingPassageFilters | undefined>(
    'reading/fetchPassagesRequest',
);
export const fetchReadingPassagesSuccess =
    createAction<PaginatedResponse<ReadingPassage>>('reading/fetchPassagesSuccess');
export const fetchReadingPassagesFailure = createAction<string>('reading/fetchPassagesFailure');

export const fetchReadingPassageByIdRequest = createAction<string>('reading/fetchPassageByIdRequest');
export const fetchReadingPassageByIdSuccess = createAction<ReadingPassage>('reading/fetchPassageByIdSuccess');
export const fetchReadingPassageByIdFailure = createAction<string>('reading/fetchPassageByIdFailure');

export const fetchPassageQuestionsRequest = createAction<string>('reading/fetchPassageQuestionsRequest');
export const fetchPassageQuestionsSuccess = createAction<ComprehensionQuestion[]>(
    'reading/fetchPassageQuestionsSuccess',
);
export const fetchPassageQuestionsFailure = createAction<string>('reading/fetchPassageQuestionsFailure');

export const fetchReadingRecommendationsRequest = createAction<number | undefined>(
    'reading/fetchRecommendationsRequest',
);
export const fetchReadingRecommendationsSuccess = createAction<ReadingRecommendation[]>(
    'reading/fetchRecommendationsSuccess',
);
export const fetchReadingRecommendationsFailure = createAction<string>('reading/fetchRecommendationsFailure');

export const rateReadingRecommendationRequest = createAction<{ recommendationId: string; rating: number }>(
    'reading/rateRecommendationRequest',
);
export const rateReadingRecommendationSuccess = createAction('reading/rateRecommendationSuccess');
export const rateReadingRecommendationFailure = createAction<string>('reading/rateRecommendationFailure');

export const fetchUserReadingProfileRequest = createAction('reading/fetchUserProfileRequest');
export const fetchUserReadingProfileSuccess = createAction<UserReadingProfile>('reading/fetchUserProfileSuccess');
export const fetchUserReadingProfileFailure = createAction<string>('reading/fetchUserProfileFailure');

export const updateUserReadingProfileRequest = createAction<{
    preferred_topics?: string[];
    preferred_passage_types?: string[];
    reading_goals?: any;
}>('reading/updateUserProfileRequest');
export const updateUserReadingProfileSuccess = createAction<UserReadingProfile>('reading/updateUserProfileSuccess');
export const updateUserReadingProfileFailure = createAction<string>('reading/updateUserProfileFailure');

// === FILTERS & UI ACTIONS ===
export const setReadingPassageFilters = createAction<ReadingPassageFilters>('reading/setFilters');
export const clearReadingPassageFilters = createAction('reading/clearFilters');
export const setCurrentFeedbackTone = createAction<FeedbackTone>('reading/setCurrentFeedbackTone');

// === STATE INTERFACE ===
export interface ReadingState {
    // Passages
    passages: ReadingPassage[];
    isLoadingPassages: boolean;
    passagesError: string | null;
    totalPassages: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;

    // Current passage
    currentPassage: ReadingPassage | null;
    isLoadingPassage: boolean;
    passageError: string | null;

    // Passage questions
    currentPassageQuestions: ComprehensionQuestion[];
    isLoadingQuestions: boolean;
    questionsError: string | null;

    // Recommendations
    recommendations: ReadingRecommendation[];
    isLoadingRecommendations: boolean;
    recommendationsError: string | null;
    isRatingRecommendation: boolean;
    ratingError: string | null;

    // User profile
    userProfile: UserReadingProfile | null;
    isLoadingProfile: boolean;
    profileError: string | null;
    isUpdatingProfile: boolean;
    updateProfileError: string | null;

    // Filters and UI state
    filters: ReadingPassageFilters;
    currentFeedbackTone: FeedbackTone;
}

// === INITIAL STATE ===
const initialState: ReadingState = {
    // Passages
    passages: [],
    isLoadingPassages: false,
    passagesError: null,
    totalPassages: 0,
    currentPage: START_PAGE,
    hasNextPage: false,
    hasPreviousPage: false,

    // Current passage
    currentPassage: null,
    isLoadingPassage: false,
    passageError: null,

    // Passage questions
    currentPassageQuestions: [],
    isLoadingQuestions: false,
    questionsError: null,

    // Recommendations
    recommendations: [],
    isLoadingRecommendations: false,
    recommendationsError: null,
    isRatingRecommendation: false,
    ratingError: null,

    // User profile
    userProfile: null,
    isLoadingProfile: false,
    profileError: null,
    isUpdatingProfile: false,
    updateProfileError: null,

    // Filters and UI state
    filters: {},
    currentFeedbackTone: 'encouraging',
};

// === SLICE DEFINITION ===
const readingSlice = createSlice({
    name: 'reading',
    initialState,
    reducers: {
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setFilters: (state, action) => {
            state.filters = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === FETCH PASSAGES ===
            .addCase(fetchReadingPassagesRequest, (state, action) => {
                state.isLoadingPassages = true;
                state.passagesError = null;
                if (action.payload) {
                    state.filters = action.payload;
                }
            })
            .addCase(fetchReadingPassagesSuccess, (state, action) => {
                state.isLoadingPassages = false;
                state.passages = action.payload.results;
                state.totalPassages = action.payload.count;
                state.hasNextPage = !!action.payload.next;
                state.hasPreviousPage = !!action.payload.previous;
            })
            .addCase(fetchReadingPassagesFailure, (state, action) => {
                state.isLoadingPassages = false;
                state.passagesError = action.payload;
            })

            // === FETCH PASSAGE BY ID ===
            .addCase(fetchReadingPassageByIdRequest, (state) => {
                state.isLoadingPassage = true;
                state.passageError = null;
            })
            .addCase(fetchReadingPassageByIdSuccess, (state, action) => {
                state.isLoadingPassage = false;
                state.currentPassage = action.payload;
            })
            .addCase(fetchReadingPassageByIdFailure, (state, action) => {
                state.isLoadingPassage = false;
                state.passageError = action.payload;
            })

            // === FETCH PASSAGE QUESTIONS ===
            .addCase(fetchPassageQuestionsRequest, (state) => {
                state.isLoadingQuestions = true;
                state.questionsError = null;
            })
            .addCase(fetchPassageQuestionsSuccess, (state, action) => {
                state.isLoadingQuestions = false;
                state.currentPassageQuestions = action.payload;
            })
            .addCase(fetchPassageQuestionsFailure, (state, action) => {
                state.isLoadingQuestions = false;
                state.questionsError = action.payload;
            })

            // === FETCH RECOMMENDATIONS ===
            .addCase(fetchReadingRecommendationsRequest, (state) => {
                state.isLoadingRecommendations = true;
                state.recommendationsError = null;
            })
            .addCase(fetchReadingRecommendationsSuccess, (state, action) => {
                state.isLoadingRecommendations = false;
                state.recommendations = action.payload;
            })
            .addCase(fetchReadingRecommendationsFailure, (state, action) => {
                state.isLoadingRecommendations = false;
                state.recommendationsError = action.payload;
            })

            // === RATE RECOMMENDATION ===
            .addCase(rateReadingRecommendationRequest, (state) => {
                state.isRatingRecommendation = true;
                state.ratingError = null;
            })
            .addCase(rateReadingRecommendationSuccess, (state) => {
                state.isRatingRecommendation = false;
            })
            .addCase(rateReadingRecommendationFailure, (state, action) => {
                state.isRatingRecommendation = false;
                state.ratingError = action.payload;
            })

            // === USER PROFILE ===
            .addCase(fetchUserReadingProfileRequest, (state) => {
                state.isLoadingProfile = true;
                state.profileError = null;
            })
            .addCase(fetchUserReadingProfileSuccess, (state, action) => {
                state.isLoadingProfile = false;
                state.userProfile = action.payload;
            })
            .addCase(fetchUserReadingProfileFailure, (state, action) => {
                state.isLoadingProfile = false;
                state.profileError = action.payload;
            })

            .addCase(updateUserReadingProfileRequest, (state) => {
                state.isUpdatingProfile = true;
                state.updateProfileError = null;
            })
            .addCase(updateUserReadingProfileSuccess, (state, action) => {
                state.isUpdatingProfile = false;
                state.userProfile = action.payload;
            })
            .addCase(updateUserReadingProfileFailure, (state, action) => {
                state.isUpdatingProfile = false;
                state.updateProfileError = action.payload;
            })

            // === FILTERS & UI ===
            .addCase(setReadingPassageFilters, (state, action) => {
                state.filters = action.payload;
                state.currentPage = 1; // Reset to first page when filters change
            })
            .addCase(clearReadingPassageFilters, (state) => {
                state.filters = {};
                state.currentPage = 1;
            })
            .addCase(setCurrentFeedbackTone, (state, action) => {
                state.currentFeedbackTone = action.payload;
            });
    },
});

export const { setCurrentPage, setFilters } = readingSlice.actions;

export default readingSlice.reducer;
